import { ComponentRef, EnvironmentInjector, Injectable, Injector, OnDestroy, createComponent } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";

import { SelectionTicketsComponent } from "src/app/tickets/solicitud/selection-tickets/selection-tickets.component";

/** El color de la tarjeta de cada tipo: cada una el suyo, distinto también de los de IA y Mantenimiento. */
export type TonoTicket = "infraestructura" | "perfil" | "capacitacion" | "mejoras";

/** Un tipo de ticket como se ofrece en «Nueva solicitud»: una tarjeta, al mismo nivel que IA y Mantenimiento. */
export interface TipoTicket {
  /** El id con que el módulo de Tickets conoce al tipo (SelectionTicketsComponent.tipos). */
  id: number;
  titulo: string;
  descripcion: string;
  ejemplos: string[];
  icono: string;
  accion: string;
  /** Su color: cada tipo el suyo, para distinguirlos de un vistazo. */
  tono: TonoTicket;
}

/** Los cuatro tipos del diálogo «Crear Ticket», con lo que se pide en cada uno. */
export const TIPOS_TICKET: TipoTicket[] = [
  {
    id: 1,
    titulo: "Infraestructura",
    descripcion: "Algo de cómputo o de sistemas que falla o que necesitas.",
    ejemplos: ["Equipo, red o un sistema", "Qué pasa y por qué lo pides", "Archivos de apoyo"],
    icono: "dns",
    accion: "Reportarlo",
    tono: "infraestructura",
  },
  {
    id: 2,
    titulo: "Perfil",
    descripcion: "Pide personal: el perfil que necesitas reclutar.",
    ejemplos: ["Puesto y cuántas personas", "Experiencia y conocimientos", "Cliente, lugar y horario"],
    icono: "person_search",
    accion: "Pedir un perfil",
    tono: "perfil",
  },
  {
    id: 3,
    titulo: "Capacitación",
    descripcion: "Una capacitación para ti o para tu equipo.",
    ejemplos: ["Qué capacitación y su detalle", "La fecha", "Quiénes la toman"],
    icono: "school",
    accion: "Pedir capacitación",
    tono: "capacitacion",
  },
  {
    id: 4,
    titulo: "Mejoras",
    descripcion: "Propón una mejora para un área o un proyecto.",
    ejemplos: ["A qué área o proyecto aplica", "Qué propones", "Archivos de apoyo"],
    icono: "tips_and_updates",
    accion: "Proponer mejora",
    tono: "mejoras",
  },
];

/**
 * Abre el formulario de un tipo de ticket igual que el diálogo «Crear
 * Ticket» del módulo de Tickets, pero sin enseñar ese diálogo (pedido de
 * Norman, 12/10/2026: en «Nueva solicitud» los tipos se escogen aquí mismo,
 * sin mover los reales; desde el 13/10/2026, como tarjetas junto a IA y
 * Mantenimiento).
 *
 * Para no copiar su lógica, crea SU componente (SelectionTicketsComponent)
 * sin ponerlo en la página y le pide lo mismo que su clic (modalTicket). Así
 * lo que abre cada tipo, y los datos que prepara para «Perfil», lo sigue
 * decidiendo el módulo de Tickets: si allá cambia, aquí cambia, y allá no se
 * toca nada.
 */
@Injectable()
export class LanzadorTickets implements OnDestroy {
  private real: ComponentRef<SelectionTicketsComponent> | null = null;

  constructor(
    private readonly entorno: EnvironmentInjector,
    private readonly inyector: Injector,
  ) {}

  ngOnDestroy(): void {
    this.suelta();
  }

  /**
   * Lo deja listo. Al crearse, el componente real pide sus catálogos
   * (prioridades, clientes y el área de quien pide), como cuando se abre su
   * diálogo: «Perfil» los necesita antes de ir a su página.
   */
  prepara(): void {
    if (this.real) {
      return;
    }
    // El componente real se cierra a sí mismo (modal.close) antes de abrir cada formulario: aquí no hay nada que cerrar.
    const sinDialogo = { close: (): void => undefined };
    this.real = createComponent(SelectionTicketsComponent, {
      environmentInjector: this.entorno,
      elementInjector: Injector.create({ providers: [{ provide: MatDialogRef, useValue: sinDialogo }], parent: this.inyector }),
    });
    // La primera detección de cambios corre su ngOnInit.
    this.real.changeDetectorRef.detectChanges();
  }

  /** El formulario de ese tipo, como lo abre el módulo de Tickets (un diálogo o su página). */
  abre(idTipo: number): void {
    this.prepara();
    this.real?.instance.modalTicket(idTipo);
  }

  suelta(): void {
    this.real?.destroy();
    this.real = null;
  }
}
